import { visitAudioSettingsSchema } from "@rosc/contracts";
import {
  type AudioVisitInput,
  createAudioVisit,
} from "@/services/audio-uploads";
import {
  beginAudioVisitAcceptance,
  completeAudioVisitSubmission,
  readAudioVisitSubmission,
  rememberAudioUpload,
} from "./audio-visit-submission";
import {
  prepareOwnedAudioFile,
  uploadOwnedAudioFile,
} from "./owned-audio-upload";

type AudioVisitSettings = Omit<AudioVisitInput, "name" | "audioUploadId">;
export const prepareAudioVisitFile = async ({
  file,
  settings,
  signal,
}: {
  file: Blob;
  settings: AudioVisitSettings;
  signal?: AbortSignal;
}) => {
  signal?.throwIfAborted();
  const body = visitAudioSettingsSchema.parse({
    ...settings,
    name: "Audio visit",
  });
  const prepared = await prepareOwnedAudioFile(file, signal);
  file = prepared.file;
  const input = prepared.input;
  const fingerprintBytes = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(
      JSON.stringify([
        "audio-file-v1",
        input.contentType,
        input.byteLength,
        input.checksumSha256,
        body.name,
        body.duration,
        body.style,
        body.format,
        body.exam,
        body.template,
        body.language,
        body.customTemplatePublicId ?? null,
        body.diarize,
        ...(body.title ? [{ title: body.title }] : []),
        ...(body.providerId ? [{ providerId: body.providerId }] : []),
        ...(body.instructions !== undefined
          ? [{ instructions: body.instructions }]
          : []),
        ...(body.skipShortcuts?.length
          ? [{ skipShortcuts: body.skipShortcuts }]
          : []),
        ...(body.recordingConsent ? [{ recordingConsent: true }] : []),
      ]),
    ),
  );
  signal?.throwIfAborted();
  const fingerprint = Array.from(new Uint8Array(fingerprintBytes), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return { file, body, input, fingerprint };
};
export type PreparedAudioVisit = Awaited<
  ReturnType<typeof prepareAudioVisitFile>
>;

export const runAudioVisitUpload = async ({
  account,
  visitKey,
  prepared,
  userUid,
  signal,
  isCurrent,
  onProgress,
}: {
  account: string;
  visitKey: string;
  prepared: PreparedAudioVisit;
  userUid: string;
  signal: AbortSignal;
  isCurrent: () => boolean;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}): Promise<{ public_id: string }> => {
  const check = (): void => {
    signal.throwIfAborted();
    if (!isCurrent())
      throw new DOMException("Audio submission session changed", "AbortError");
  };
  check();
  let state = readAudioVisitSubmission(account);
  if (
    !state ||
    state.visitKey !== visitKey ||
    state.fingerprint !== prepared.fingerprint
  )
    throw Error(
      "This audio submission is no longer active. Check Visits before trying again.",
    );
  if (state.kind === "accepted") return { public_id: state.visitPublicId };
  const identity = { userUid, signal };
  if (state.kind === "intent" || state.kind === "upload") {
    let ready = state;
    await uploadOwnedAudioFile({
      file: prepared.file,
      input: prepared.input,
      identity: { ...identity, key: state.uploadKey },
      uploadId: state.kind === "upload" ? state.uploadId : undefined,
      onCreated: (id) => {
        if (ready.kind === "intent")
          ready = rememberAudioUpload(account, ready, id);
        else if (ready.uploadId !== id)
          throw Error("Upload recovery identity changed");
      },
      isCurrent,
      onProgress,
    });
    check();
    if (ready.kind !== "upload") throw Error("Upload identity was not saved");
    state = beginAudioVisitAcceptance(account, ready);
  }
  check();
  const result = await createAudioVisit(
    { ...prepared.body, audioUploadId: state.uploadId },
    { ...identity, key: state.visitKey },
  );
  check();
  completeAudioVisitSubmission(account, state, result.public_id);
  return result;
};
